// The mock draft's kit, as data (MOCK_DRAFT.md §4, BACKLOG D16).
//
// EVERYTHING A MOCK NEEDS, IN ONE READ. A solo mock against bots runs in the
// client, on the same pure room the live draft runs in (`@fsp/draft-room`), so
// a mock costs the server one read however many picks it makes. This is what
// that read answers: the league's draft as the room would run it, a bot in
// every seat, the pool with what a drafter reads about each player, and the
// engine's context as data. A client puts the person in the seat they choose
// and hands the rest to the room.
//
// ONLY THE CALLER'S OWN PLAN. The house ranking, the caps and the positions
// are the league's; every manager's priorities are their own, and a mock seats
// a house bot where a leaguemate sits rather than drafting from their list.

import { z } from 'zod';
import {
  DraftBoardCell,
  DraftSeasonLine,
  DraftSeat,
  LeagueRef,
  PlayerSummary,
  TeamRef,
} from './views';

/** How a bot drafts and what it is called: the engine's `AiManager`, as data. */
export const DraftBotManager = z.object({
  presetId: z.string(),
  name: z.string(),
  /** One line in its own voice, shown on the seat. */
  voice: z.string(),
  /** How well it plays, 0 to 100. */
  skill: z.number(),
  /** Its style, each 0 to 1: `activity` is its think time in a draft, `risk` blurs a pick. */
  dials: z.object({
    trashTalk: z.number(),
    aggression: z.number(),
    risk: z.number(),
    loyalty: z.number(),
    activity: z.number(),
  }),
});
export type DraftBotManager = z.infer<typeof DraftBotManager>;

/** One seat, in round-one order, with the bot that drafts it in a mock. */
export const DraftKitSeat = z.object({
  teamId: z.string(),
  /** 1-based. */
  slot: z.number().int(),
  team: TeamRef,
  /** Every seat has one: the league's own bot where it has one, a house bot where a person sits. */
  bot: z.object({ manager: DraftBotManager, difficulty: z.string() }),
});
export type DraftKitSeat = z.infer<typeof DraftKitSeat>;

/** One player in the pool, as the room shows him (`DraftPlayerRow`'s research fields). */
export const DraftKitPlayer = z.object({
  id: z.string(),
  summary: PlayerSummary,
  /** His primary position, the board's. */
  position: z.string(),
  /** On the league's own ranking, 1-based. */
  rank: z.number().int(),
  positionRank: z.number().int(),
  tier: z.number().int(),
  bye: z.number().int().nullable(),
  last: DraftSeasonLine.nullable(),
  /** His worth over the player the league could have had instead: what the recap grades by. */
  value: z.number(),
  /**
   * What a bot's draft style knows about him (AI_MANAGERS.md §4.7): his pro
   * club, its bye, a rookie, his seasons, the injury report (0 not on it, 1 a
   * warning, 2 likely out or out), his games last season, his first name.
   * Absent from a kit made before 2026-10-15, whose bots draft by the list.
   */
  facts: z
    .object({
      club: z.string().nullable(),
      bye: z.number().int().nullable(),
      rookie: z.boolean(),
      years: z.number().int().nullable(),
      hurt: z.union([z.literal(0), z.literal(1), z.literal(2)]),
      games: z.number().int().nullable(),
      first: z.string().nullable(),
    })
    .optional(),
});
export type DraftKitPlayer = z.infer<typeof DraftKitPlayer>;

/** The caller's priorities, in the shape the resolver takes (`ResolvePlan`). */
export const DraftKitPlan = z.object({
  ordering: z.enum(['overall', 'by-position']),
  overall: z.array(z.string()),
  byPosition: z.record(z.string(), z.array(z.string())),
  limits: z.record(z.string(), z.object({ min: z.number().int(), max: z.number().int() })),
  roundPlan: z.array(z.string()),
  doNotDraft: z.array(z.string()),
  /** An auction league's prices, what my seat bids up to on auto (AUCTION.md §7). */
  prices: z.record(z.string(), z.number().int()).optional(),
});
export type DraftKitPlan = z.infer<typeof DraftKitPlan>;

export const DraftKit = z.object({
  /** The league, with the caller's own team in `myTeamId`. */
  league: LeagueRef,
  type: z.enum(['snake', 'linear', 'third-round-reversal']),
  /**
   * How the league drafts. Only a `live` draft has a pick clock of its own;
   * for the others `pickSeconds` and `whenTheClockExpires` are the rules'
   * defaults, and a client offers a clock as practice rather than as the
   * league's.
   */
  draftMode: z.enum(['autopick', 'manual', 'live']),
  rounds: z.number().int(),
  /** Seconds a pick: the league's own for a live draft, the rules' default otherwise. */
  pickSeconds: z.number().int(),
  /** What a clock does at zero: the league's own for a live draft. */
  whenTheClockExpires: z.enum(['queue-then-rankings', 'rankings', 'off']),
  /** The caller's seat in the round-one order as things stand; null for a member without a team. */
  mySlot: z.number().int().nullable(),
  /** True once the league's order is fixed; until then `mySlot` can still move. */
  orderFixed: z.boolean(),
  seats: z.array(DraftKitSeat),
  /** The whole pool, in the order the draft runs on. */
  players: z.array(DraftKitPlayer),
  /** What the ranking was built from, in words (`DraftView.basis`). */
  basis: z.string(),
  /** Each pro club's bye this season. */
  byeWeeks: z.record(z.string(), z.number().int()),
  /** The league's roster slots, in its order. */
  slots: z.array(
    z.object({
      slot: z.string(),
      slotName: z.string(),
      count: z.number().int(),
      eligible: z.array(z.string()),
      kind: z.string(),
    }),
  ),
  /** Starting slots that take one position: what a team's needs are counted against. */
  needs: z.array(z.object({ position: z.string(), count: z.number().int() })),
  /**
   * An auction league's rules, as a mock practices them (AUCTION.md §6.4): a
   * slow auction's hours become an evening's clocks, one player at a time.
   * Null for a pick draft, whose order is `type`; an auction's `type` is
   * `linear`, its board's.
   */
  auction: z
    .object({
      budget: z.number().int(),
      minBid: z.number().int(),
      nominationSeconds: z.number().int(),
      bidSeconds: z.number().int(),
      resetSeconds: z.number().int(),
      lotsAtOnce: z.number().int(),
    })
    .nullable()
    .default(null),
  /** The engine's context as data (`DraftContextParts`), with the caller's plan alone. */
  context: z.object({
    rankings: z.array(z.string()),
    positions: z.record(z.string(), z.string()),
    starterNeeds: z.record(z.string(), z.number().int()),
    maxPerPosition: z.record(z.string(), z.number().int()),
    leagueCaps: z.record(z.string(), z.number().int()),
    latePositions: z.array(z.string()),
    /** What the bots' choices are keyed on; a client varies it per mock so no two run alike. */
    seed: z.string(),
    myPlan: DraftKitPlan.nullable(),
    /**
     * What the bots' draft styles read (AI_MANAGERS.md §4.7): the league's
     * sport, the league's id their tastes are drawn by (so a mock's bots have
     * the tastes they will have at the draft), and each pro club's name and
     * division. Absent, the bots draft by the list.
     */
    sport: z.string().optional(),
    styleSeed: z.string().optional(),
    clubs: z
      .record(z.string(), z.object({ name: z.string(), division: z.string().nullable() }))
      .optional(),
  }),
  preparedAt: z.string(),
});
export type DraftKit = z.infer<typeof DraftKit>;

// ── Kept mocks (MOCK_DRAFT.md §6.1) ────────────────────────────────────────
//
// EVERY MOCK THAT FINISHES IS KEPT, the last `MOCKS_KEPT` of a manager's in a
// league, each for `MOCK_KEPT_DAYS`, and nobody else sees one until its
// manager shares it to the league's chat. The client that ran it sends the
// record once it ends; the server checks the draft's shape and keeps the grades
// as sent, because they come from the recap's own grader over the kit the mock
// ran on, and grading again would mean preparing the whole board per mock.

/** How many finished mocks a manager keeps in one league: the newest push the oldest out. */
export const MOCKS_KEPT = 20;
/** How long a kept mock lasts, in days from when it ran. */
export const MOCK_KEPT_DAYS = 180;

/** The recap's grades, best first (`@fsp/draft`'s `Grade`). */
export const MockGrade = z.enum(['A', 'A-', 'B+', 'B', 'B-', 'C+', 'C']);
export type MockGrade = z.infer<typeof MockGrade>;

/** Who decided a pick, as the board says it (`DraftBoardCell.by`). */
const MockPickBy = z.enum(['user', 'queue', 'plan', 'rankings', 'ai', 'keeper']);

/** One team's grade in a mock, from the recap's grader. */
export const MockTeamGrade = z.object({
  teamId: z.string().min(1).max(64),
  /** 1 for the most value drafted. */
  rank: z.number().int().min(1),
  grade: MockGrade,
});
export type MockTeamGrade = z.infer<typeof MockTeamGrade>;

/**
 * A finished mock, as a client sends it to be kept (`POST …/draft/mocks`).
 *
 * IDS, NOT A COPY OF THE DIRECTORY: a player's name is looked up when the mock
 * is read. `key` is the client's own id for the run, so sending it again — a
 * retry after a dropped answer — is the same mock and not a second one.
 */
export const SaveMockRequest = z.object({
  key: z.string().min(1).max(64),
  /** The seat I drafted from, 1-based in the mock's round-one order. */
  slot: z.number().int().min(1),
  bots: z.enum(['quick', 'instant']),
  clock: z.enum(['none', 'league']),
  /** The league's team ids, in the mock's round-one order. */
  order: z.array(z.string().min(1).max(64)).min(2).max(20),
  /** Who drafted each seat, in the same order: a bot's name, a friend's in a league mock, or null for mine. */
  drafters: z.array(z.string().min(1).max(80).nullable()).min(2).max(20),
  /**
   * The seats a person drafted in a league mock (MOCK_DRAFT.md §6.2), by team
   * id, mine left out: their drafters are people, not bots. Empty for a solo
   * mock, where every other seat is a bot, and so absent is empty.
   */
  people: z.array(z.string().min(1).max(64)).max(20).default([]),
  /** Every pick, in order. */
  picks: z
    .array(
      z.object({
        playerId: z.string().min(1).max(64),
        by: MockPickBy,
        /** What an auction's winner paid; absent for a pick (AUCTION.md §6.4). */
        price: z.number().int().min(0).max(100_000).optional(),
      }),
    )
    .min(1)
    .max(800),
  /** Every team's grade. */
  grades: z.array(MockTeamGrade).min(2).max(20),
  /** The recap's line about my draft. */
  line: z.string().max(400),
  /** The overall number of my pick that went furthest past where the board had him; null for none. */
  best: z.number().int().min(1).nullable(),
  /** The recap's headline over every team. */
  headline: z.string().max(300),
});
export type SaveMockRequest = z.infer<typeof SaveMockRequest>;

/** A kept mock in my list: enough to tell one from another. */
export const SavedMockSummary = z.object({
  id: z.string(),
  /** When it ran (ISO). */
  at: z.string(),
  slot: z.number().int(),
  teams: z.number().int(),
  rounds: z.number().int(),
  grade: MockGrade,
  rank: z.number().int(),
  bots: z.enum(['quick', 'instant']),
  clock: z.enum(['none', 'league']),
  /** The first three players its manager drafted. */
  firstPicks: z.array(PlayerSummary),
  /** When it was shared to the league's chat (ISO), or null for not shared. */
  sharedAt: z.string().nullable(),
});
export type SavedMockSummary = z.infer<typeof SavedMockSummary>;

/** My kept mocks in a league, newest first (`GET …/draft/mocks`). */
export const SavedMocksView = z.object({
  leagueId: z.string(),
  /** `MOCKS_KEPT` and `MOCK_KEPT_DAYS`, so a client says what is kept without knowing the numbers. */
  kept: z.number().int(),
  keptDays: z.number().int(),
  mocks: z.array(SavedMockSummary),
});
export type SavedMocksView = z.infer<typeof SavedMocksView>;

/**
 * One kept mock, whole (`GET …/draft/mocks/:mockId`): for its manager, or for
 * any member of the league once it is shared. The seats and the board are the
 * draft room's own shapes, so a client draws them as it draws the room.
 */
export const SavedMock = SavedMockSummary.extend({
  /** The display name of whoever ran it. */
  owner: z.string(),
  /** The reader ran it: the one who may share or delete it. */
  mine: z.boolean(),
  sport: z.string(),
  /** The pick order, or `auction`, whose board is each team's buys in order with their prices. */
  type: z.enum(['snake', 'linear', 'third-round-reversal', 'auction']),
  /** The team its manager drafted for. */
  ownerTeamId: z.string(),
  /**
   * In the mock's round-one order. A bot's seat carries the bot's name as its
   * owner; its manager's seat carries theirs, and `team.isMine` for its
   * manager alone. No seat is connected or on auto: the mock is over.
   */
  seats: z.array(DraftSeat),
  board: z.array(DraftBoardCell),
  grades: z.array(MockTeamGrade),
  line: z.string(),
  best: z.number().int().nullable(),
  headline: z.string(),
});
export type SavedMock = z.infer<typeof SavedMock>;

// ── The league mock, together (MOCK_DRAFT.md §6.2) ────────────────────────
//
// ONE OPEN AT A TIME IN A LEAGUE, run in the live room's worker under a key of
// its own (`mock:<mockId>`), seats taken in its lobby, bots in the rest when it
// starts, and every person's seat kept in their mocks when it ends.

/** The clocks a league mock offers: an evening's, never a slow draft's. */
export const LEAGUE_MOCK_CLOCKS = [30, 60, 90] as const;
/** How long a lobby nobody starts stays open, in minutes. */
export const LEAGUE_MOCK_LOBBY_MINUTES = 120;
/**
 * The longest a league mock stays paused: then its room goes on by itself, so
 * a host who pauses and leaves never strands everybody else in it.
 */
export const LEAGUE_MOCK_PAUSE_MINUTES = 15;

/** Open a league mock, or join the one already open (`POST …/draft/together`). */
export const OpenLeagueMockRequest = z.object({
  pickSeconds: z.union([z.literal(30), z.literal(60), z.literal(90)]),
});
export type OpenLeagueMockRequest = z.infer<typeof OpenLeagueMockRequest>;

/** A league mock, as anybody in the league reads it. */
export const LeagueMockSummary = z.object({
  id: z.string(),
  /** The display name of whoever opened it, who runs it. */
  openedBy: z.string(),
  /** The reader opened it. */
  mine: z.boolean(),
  pickSeconds: z.number().int(),
  status: z.enum(['lobby', 'live']),
  openedAt: z.string(),
  /** When a lobby nobody starts closes (ISO); null once it is under way. */
  closesAt: z.string().nullable(),
});
export type LeagueMockSummary = z.infer<typeof LeagueMockSummary>;

/** The league mock open now, if there is one (`GET …/draft/together`). */
export const LeagueMockView = z.object({
  leagueId: z.string(),
  open: LeagueMockSummary.nullable(),
});
export type LeagueMockView = z.infer<typeof LeagueMockView>;
